import { useEffect, useMemo, useState } from "react";
import type { MartinLabActionKey, MartinLabCommandResponse, MartinLabPersonaKey, MartinLabWorkspace } from "../../src/contracts/product-lab";
import { martinLabActionLabels, martinLabEventLabel } from "./product-lab-labels";

const allActions = Object.keys(martinLabActionLabels) as MartinLabActionKey[];

const loadLab = async (customerKey: string): Promise<MartinLabWorkspace> => {
  const response = await fetch(`/api/v1/platform/customers/${encodeURIComponent(customerKey)}/lab`, {
    credentials: "same-origin",
    headers: { accept: "application/json" }
  });
  if (!response.ok) throw new Error("This customer Product Lab is unavailable.");
  return response.json() as Promise<MartinLabWorkspace>;
};

const commandEnvelope = (expectedRevision: number) => ({
  commandId: crypto.randomUUID(),
  idempotencyKey: `lab:${crypto.randomUUID()}`,
  expectedRevision,
  correlationId: crypto.randomUUID()
});

const postLab = async (customerKey: string, path: "actions" | "reset", body: unknown): Promise<MartinLabCommandResponse> => {
  const init: RequestInit = {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body)
  };
  const route = `/api/v1/platform/customers/${encodeURIComponent(customerKey)}/lab/${path}`;
  let response = await fetch(route, init);
  if ([502, 503, 504].includes(response.status)) response = await fetch(route, init);
  const payload = await response.json() as MartinLabCommandResponse & { code?: string };
  if (!response.ok) {
    if (payload.workspace !== undefined) {
      const message = payload.code === "revision_conflict"
        ? "The lab changed in another window. Its latest state has been loaded."
        : payload.code === "idempotency_conflict"
          ? "That command identity was already used for different content. Nothing was changed."
          : "That action is not valid for this persona or workflow state.";
      throw Object.assign(new Error(message), { workspace: payload.workspace });
    }
    throw new Error("The Product Lab command could not be applied.");
  }
  return payload;
};

const steps = [
  ["Solicitation", "solicitation"], ["Supplier RFQ", "rfq"], ["Supplier response", "supplierResponse"],
  ["Award", "award"], ["Supplier PO", "supplierPo"], ["Field report", "fieldReport"],
  ["Goods readiness", "goodsReadiness"], ["Service readiness", "serviceReadiness"],
  ["Portal share", "portalShare"], ["Billing", "billing"], ["QuickBooks", "quickbooks"]
] as const;

export function MartinProductLab({ customerKey, customerName, operatorName, back }: Readonly<{
  customerKey: string;
  customerName: string;
  operatorName: string;
  back: () => void;
}>) {
  const [workspace, setWorkspace] = useState<MartinLabWorkspace>();
  const [personaKey, setPersonaKey] = useState<MartinLabPersonaKey>("martin_owner");
  const [busy, setBusy] = useState<string>();
  const [error, setError] = useState<string>();
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let current = true;
    setWorkspace(undefined); setError(undefined);
    void loadLab(customerKey).then((value) => { if (current) setWorkspace(value); })
      .catch((reason: unknown) => { if (current) setError(reason instanceof Error ? reason.message : "Lab unavailable."); });
    return () => { current = false; };
  }, [customerKey, attempt]);
  const persona = useMemo(() => workspace?.personas.find((item) => item.key === personaKey), [workspace, personaKey]);

  const run = async (actionKey: MartinLabActionKey) => {
    if (workspace === undefined) return;
    setBusy(actionKey); setError(undefined);
    try {
      const result = await postLab(customerKey, "actions", { ...commandEnvelope(workspace.revision), personaKey, actionKey });
      setWorkspace(result.workspace);
    } catch (reason) {
      if (reason instanceof Error && "workspace" in reason) setWorkspace((reason as Error & { workspace: MartinLabWorkspace }).workspace);
      setError(reason instanceof Error ? reason.message : "Action failed.");
    } finally { setBusy(undefined); }
  };

  const reset = async () => {
    if (workspace === undefined) return;
    setBusy("reset"); setError(undefined);
    try { setWorkspace((await postLab(customerKey, "reset", commandEnvelope(workspace.revision))).workspace); }
    catch (reason) {
      if (reason instanceof Error && "workspace" in reason) setWorkspace((reason as Error & { workspace: MartinLabWorkspace }).workspace);
      setError(reason instanceof Error ? reason.message : "Reset failed.");
    } finally { setBusy(undefined); }
  };

  if (error !== undefined && workspace === undefined) return <section className="empty-state"><p className="eyebrow">SEED / NON-PRODUCTION</p><h1>Product Lab unavailable</h1><p>{error}</p><div className="button-row"><button data-control-id="admin.lab.retry" className="primary" onClick={() => setAttempt((value) => value + 1)}>Retry lab read</button><button data-control-id="admin.lab.error-back" className="secondary" onClick={back}>All Product Labs</button></div></section>;
  if (workspace === undefined) return <section className="loading-card" role="status">Preparing the isolated customer domain replay…</section>;
  return <>
    <button data-control-id="admin.lab.back" className="back" onClick={back}>← All Product Labs</button>
    <header className="page-header"><div><p className="eyebrow">SEED / NON-PRODUCTION · domain-service replay</p><h1>{customerName} Product Lab</h1><p>{workspace.state.reference} · {workspace.state.title}</p></div><button data-control-id="admin.lab.reset" className="secondary" disabled={busy !== undefined} title={busy === undefined ? "Reset only this isolated seed replay." : "Wait for the current seed command to finish."} onClick={() => void reset()}>Reset domain replay</button></header>
    <section className="lab-safety"><strong>Not a configuration twin</strong><span>This isolated replay exercises current shared domain rules, but it does not invoke production command handlers or prove the customer&apos;s pinned release and configuration.</span><i>Revision {workspace.revision}</i></section>
    <p className="lab-unavailable" role="note"><strong>Executable seed-domain replay only.</strong> {workspace.runtime.limitations.join(" ")}</p>
    <p className="lab-unavailable" role="note"><strong>QuickBooks Online is not connected.</strong> No accounting success is simulated. A handoff becomes testable only after the real adapter and sandbox reconciliation evidence exist.</p>
    <section className="persona-panel panel"><div><p className="eyebrow">Apply the seed role policy</p><h2>Persona lens</h2></div><select data-control-id="admin.lab.persona" value={personaKey} onChange={(event) => setPersonaKey(event.target.value as MartinLabPersonaKey)}>{workspace.personas.map((item) => <option key={item.key} value={item.key}>{item.label} · {item.organization}</option>)}</select><p><strong>{persona?.scope}</strong><span className="operator-truth"> Attributable operator remains {operatorName}; the persona never impersonates a customer identity.</span></p></section>
    {error !== undefined && <p className="error lab-error" role="alert">{error}</p>}
    <section className="lab-workflow">{steps.map(([label, key]) => <article key={key}><span>{label}</span><strong>{key === "quickbooks" ? "not connected" : workspace.state[key].replaceAll("_", " ")}</strong></article>)}</section>
    <section className="two-col lab-columns"><article className="panel"><p className="eyebrow">Domain-service replay</p><h2>Actions available to this seed persona</h2><div className="lab-actions">{allActions.map((action) => {
      const inScope = persona?.allowedActions.includes(action) === true;
      const sharedRule = workspace.runtime.sharedDomainRuleActions.includes(action);
      const reason = busy !== undefined ? "Wait for the current seed command to finish."
        : inScope ? undefined : "This action is outside the selected seed persona's scope.";
      return <button data-control-id={`admin.lab.action.${action}`} data-control-family="admin.lab.action.*" key={action} disabled={busy !== undefined || !inScope} title={reason} onClick={() => void run(action)}><span>{martinLabActionLabels[action]}</span><small>{inScope ? (sharedRule ? "Apply through shared domain rules" : "Apply lab-only orchestration") : "Outside persona scope"}</small></button>;
    })}</div></article><article className="panel"><p className="eyebrow">Canonical command evidence</p><h2>Scenario timeline</h2>{workspace.events.length === 0 ? <p>No domain actions have been applied since reset.</p> : <ol className="lab-events">{workspace.events.map((event) => <li key={event.id}><strong>{martinLabEventLabel(event.actionKey)}</strong><span>{workspace.personas.find((item) => item.key === event.personaKey)?.label} · revision {event.revision} · correlation {event.correlationId?.slice(0, 8) ?? "legacy"}</span></li>)}</ol>}</article></section>
  </>;
}
